import React, { useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';

const appStoreUrl = 'https://apps.apple.com/app/chordbaker/id6748584837'; // Replace with your App Store URL
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.chordbaker'; // Replace with your Play Store URL

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  footer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.xs, // Reduced from md to sm for less room
    paddingHorizontal: theme.spacing.sm,
    backgroundColor: theme.colors.background,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentWrapperLarge: {
    flexDirection: 'row' as const,
  },
  text: {
    fontSize: theme.typography.sizes.sm,
    textAlign: 'center' as const,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  textSmall: {
    fontSize: theme.typography.sizes.sm, // Smaller font on small screens
  },
  textLarge: {
    marginBottom: 0,
    marginRight: theme.spacing.lg,
    fontSize: theme.typography.sizes.md, // Increased from sm to md for bigger font on large screens
  },
  linksRow: {
    display: 'flex',
    gap: theme.spacing.md,
    marginTop: theme.spacing.sm,
    justifyContent: 'center',
  },
  privacyLink: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textDecoration: 'none',
    textAlign: 'center' as const,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing.md,
  },
  buttonContainerLarge: {
    gap: theme.spacing.lg,
  },
  storeImage: {
    height: 32, // Using the same height as theme.sizes.icon.xl
  },
  storeImageSmall: {
    height: 28, // Smaller buttons on small screens
  },
  storeImageLarge: {
    height: 32, // Bigger buttons on large screens
  },
});

export default function Footer() {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  // Initialize to true (desktop layout) to prevent flash on most devices
  const [isLargeScreen, setIsLargeScreen] = React.useState(true);

  React.useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 640);
    };

    // Set initial state immediately
    setIsLargeScreen(window.innerWidth >= 640);
    
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <footer style={styles.footer}>
      <div 
        style={{
          ...styles.contentWrapper,
          ...(isLargeScreen ? styles.contentWrapperLarge : {}),
        }}
      >
        <span 
          style={{
            ...styles.text,
            ...(isLargeScreen ? styles.textLarge : styles.textSmall),
          }}
        >
          Also available on the App Store and Google Play.
        </span>
        <div style={{
          ...styles.buttonContainer,
          ...(isLargeScreen ? styles.buttonContainerLarge : {}),
        }}>
          <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{
                ...styles.storeImage,
                ...(isLargeScreen ? styles.storeImageLarge : styles.storeImageSmall),
              }}
            />
          </a>
          <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              style={{
                ...styles.storeImage,
                ...(isLargeScreen ? styles.storeImageLarge : styles.storeImageSmall),
              }}
            />
          </a>
        </div>
      </div>
      <div style={styles.linksRow}>
        <a href="/privacy" style={styles.privacyLink}>Privacy Policy</a>
        <a href="/delete-account" style={styles.privacyLink}>Delete Account</a>
      </div>
    </footer>
  );
}
